import {isFeatureEnabled} from '@github-ui/feature-flags'
import {HybridMarkdownEditor} from '@github-ui/hybrid-markdown-editor'
import type {
  HybridMarkdownEditorProps,
  HybridMarkdownEditorRef,
  MarkdownViewMode,
} from '@github-ui/hybrid-markdown-editor/types'
import {sendEvent} from '@github-ui/hydro-analytics'
import {ViewSwitch} from '@github-ui/markdown-editor-view-switch'
import {MarkdownViewer} from '@github-ui/markdown-viewer'
import {noop} from '@github-ui/noop'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {useIgnoreKeyboardActionsWhileComposing} from '@github-ui/use-ignore-keyboard-actions-while-composing'
import {useSlots} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {Actions} from './Actions'
import {ErrorBanner} from './ErrorBanner'
import {CoreFooter, Footer} from './Footer'
import {FormattingTools} from './FormattingTools'
import {Label} from './Label'
import styles from './MarkdownEditor.module.css'
import {FormattingToolsContext, MarkdownEditorContext} from './MarkdownEditorContext'
import {MarkdownInput} from './MarkdownInput'
import {SavedRepliesContext, type SavedRepliesHandle, type SavedReply} from './SavedReplies'
import type {SuggestionOptions} from './suggestions'
import type {Emoji} from './suggestions/use-emoji-suggestions'
import type {Mentionable} from './suggestions/use-mention-suggestions'
import type {Reference} from './suggestions/use-reference-suggestions'
import {CoreToolbar, DefaultToolbarButtons, Toolbar} from './Toolbar'
import {useIndenting} from './use-indenting'
import {useInputManagement} from './use-input-management'
import {useListEditing} from './use-list-editing'
import {usePreserveSpacingIfWithinSuggestedChange} from './use-preserve-spacing-if-within-suggested-change'
import {usePreview} from './use-preview'
import {type FileType, type FileUploadResult, useFileHandling} from './UseFileHandling'
import {isModifierKey} from './utils'

// Props that only apply to the hybrid editor and don't exist on `MarkdownEditor`
type HybridEditorProps = Pick<HybridMarkdownEditorProps, 'customToolbarButtons'>

export type MarkdownEditorProps = {
  ref?: React.Ref<MarkdownEditorHandle>
  /** Current value of the editor as a multiline markdown string. */
  value: string
  /** Called when the value changes. */
  onChange: (newMarkdown: string) => void
  /** Called when the textarea gains focus. */
  onInputFocus?: () => void
  /**
   * Accepts Markdown and returns rendered HTML. To prevent XSS attacks,
   * the HTML should be sanitized and/or come from a trusted source.
   */
  onRenderPreview: (markdown: string) => Promise<SafeHTMLString>
  children: React.ReactNode
  /** Disable the editor and all related buttons. Users can still switch between preview & edit modes. */
  disabled?: boolean
  /** Placeholder text to show when the editor is empty. By default, no placeholder will be shown. */
  placeholder?: string
  /** Maximum number of characters the markdown can hold (includes formatting characters like `*`). */
  maxLength?: number
  /**
   * Force the editor to take up the full height of the container and disallow resizing. Only
   * use when the container height is tall enough that the user will never want to expand the
   * input further, ie when it takes the full height of the viewport.
   */
  fullHeight?: boolean
  /** ID of the describing element. */
  'aria-describedby'?: string
  /** ID of the labelling element. */
  labelledBy?: string
  /** Optionally control the view mode. If uncontrolled, leave this `undefined`. */
  viewMode?: MarkdownViewMode
  /** If `viewMode` is controlled, this will be called on change. */
  onChangeViewMode?: (newViewMode: MarkdownViewMode) => void
  /**
   * Called when the user presses `Ctrl`/`Cmd` + `Enter`. Should almost always be wired to
   * the same event as clicking the primary `actionButton`.
   */
  onPrimaryAction?: () => void
  /**
   * Minimum number of visible lines of text in the editor.
   * @default 5
   */
  minHeightLines?: number
  /**
   * Maximum number of visible lines of text in the editor. Has no effect if `fullHeight = true`.
   * @default 35
   */
  maxHeightLines?: number
  /** Array of all possible emojis to suggest. Leave `undefined` to disable emoji autocomplete. */
  emojiSuggestions?: SuggestionOptions<Emoji>
  /**
   * Skin tone preference used for rendering applicable emoji suggestions between 1-5.
   * 0 can be used as a default value.
   * See: https://github.com/github/g-emoji-element?tab=readme-ov-file#skin-tones for more information.
   */
  emojiTone?: number
  /** Array of all possible mention suggestions. Leave `undefined` to disable `@`-mention autocomplete. */
  mentionSuggestions?: SuggestionOptions<Mentionable>
  /** Array of all possible references to suggest. Leave `undefined` to disable `#`-reference autocomplete. */
  referenceSuggestions?: SuggestionOptions<Reference>
  /**
   * Uploads a file to a hosting service and returns the URL. If not provided, file uploads
   * will be disabled.
   */
  onUploadFile?: (file: File, onProgress?: (progressBytes: number) => void) => Promise<FileUploadResult>
  /**
   * Array of allowed file types. If `onUploadFile` is defined but this array is not, all
   * file types will be accepted. You can still reject file types by rejecting the `onUploadFile`
   * promise, but setting this array provides a better user experience by preventing the
   * upload in the first place.
   */
  acceptedFileTypes?: FileType[]
  /**
   * Callback when files are rejected due to unsupported file types.
   * Useful for analytics tracking.
   */
  onFilesRejected?: (files: File[], rejectedTypes: string[]) => void
  /** Control whether the editor font is monospace. */
  monospace?: boolean
  /** Control whether the input is required. */
  required?: boolean
  /** The name that will be given to the `textarea`. */
  name?: string
  /** To enable the saved replies feature, provide an array of replies. */
  savedReplies?: SavedReply[]
  /** Callback when the saved replies picker is opened */
  onSavedRepliesOpen?: () => void
  /**
   * Control whether URLs are pasted as plain text instead of as formatted links (if the
   * user has selected some text before pasting). Defaults to `false` (URLs will paste as
   * links). This should typically be controlled by user settings.
   *
   * Users can always toggle this behavior by holding `shift` when pasting.
   */
  pasteUrlsAsPlainText?: boolean
  /**
   * Optional banner to display inside the editor, below the input area.
   * Typically used for error, info, or warning messages. Use `MarkdownEditor.ErrorBanner`
   * for error messages to get consistent styling and accessibility.
   *
   * Note: File upload errors (from drag-and-drop/paste) take priority over this banner
   * and will be displayed instead when present.
   *
   * @example
   * ```tsx
   * <MarkdownEditor
   *   banner={errorMessage ? <MarkdownEditor.ErrorBanner>{errorMessage}</MarkdownEditor.ErrorBanner> : undefined}
   * />
   * ```
   */
  banner?: React.ReactNode
  /** Use hovercards for team mentions in preview mode */
  teamHovercardsEnabled?: boolean
  /** Optional class name */
  className?: string
  /** When true, displays a skeleton loading state instead of the textarea. */
  generatingText?: boolean
  /**
   * Props that only apply to the new hybrid Markdown editing experience. Will be forwarded through to the
   * `HybridMarkdownEditor` component and will not have any effect unless hybrid mode is selected.
   */
  // ESLint false positive
  // eslint-disable-next-line @eslint-react/no-unused-props
  hybridEditorProps?: HybridEditorProps
}

type ClassicMarkdownEditorProps = {
  containerRef: React.RefObject<HTMLDivElement | null>
  viewMode: 'edit' | 'preview'
  footer: React.ReactNode
}

const handleBrand = Symbol()

export interface MarkdownEditorHandle {
  /** Focus on the markdown textarea (has no effect in preview mode). */
  focus: (options?: FocusOptions) => void
  /** Move the cursor to the end of the input. */
  moveCursorToEnd: () => void
  /** Scroll to the editor. */
  scrollIntoView: (options?: ScrollIntoViewOptions) => void
  /** Reset the value and height calculations to initial state. `onChange` will be called with an empty string. */
  reset: () => void
  /**
   * Reference to the input element. Can be used to measure position
   * for positioning floating panels relative to the input area.
   * For classic editor this is the textarea, for hybrid editor this is the container div.
   */
  inputRef: React.RefObject<HTMLElement | null>
  /**
   * This 'fake' member prevents other types from being assigned to this, thus
   * disallowing broader ref types like `HTMLTextAreaElement`.
   * @private
   */
  [handleBrand]: undefined
}

// Determine which editor to use
function getEditorType(viewMode: MarkdownViewMode): 'hybrid' | 'classic' {
  const hybridEditorEnabled = isFeatureEnabled('markdown_experience_hybrid_editor')
  return viewMode === 'hybrid' && hybridEditorEnabled ? 'hybrid' : 'classic'
}

/**
 * We want to switch editors from preview mode on cmd/ctrl+shift+P. But in preview mode,
 * there's no input to focus so we have to bind the event to the document. If there are
 * multiple editors, we want the most recent one to switch to preview mode to be the one
 * that we switch back to edit mode, so we maintain a LIFO stack of IDs of editors in
 * preview mode.
 */
let editorsInPreviewMode: string[] = []

/**
 * Markdown textarea with controls & keyboard shortcuts.
 */
const ClassicMarkdownEditor = ({
  ref,
  value,
  onInputFocus,
  disabled = false,
  placeholder,
  maxLength,
  'aria-describedby': describedBy,
  labelledBy,
  fullHeight,
  onRenderPreview,
  className,
  onPrimaryAction,
  viewMode,
  onChangeViewMode,
  minHeightLines = 5,
  maxHeightLines = 35,
  emojiSuggestions,
  emojiTone,
  mentionSuggestions,
  referenceSuggestions,
  monospace = false,
  required = false,
  name,
  children,
  savedReplies,
  pasteUrlsAsPlainText = false,
  teamHovercardsEnabled = false,
  onSavedRepliesOpen = noop,
  banner,
  containerRef,
  onChange,
  onUploadFile,
  acceptedFileTypes,
  onFilesRejected,
  footer,
  generatingText,
}: MarkdownEditorProps & ClassicMarkdownEditorProps) => {
  const [slots] = useSlots(children, {
    toolbar: Toolbar,
  })
  const id = useId()

  const useOnInput = isFeatureEnabled('mardown_editor_use_on_input')
  const hybridEditorEnabled = isFeatureEnabled('markdown_experience_hybrid_editor')
  const toolbarRedesignEnabled = isFeatureEnabled('markdown_experience_toolbar_redesign')

  const {inputRef, inputKey, reset, emitChange, onInputChange} = useInputManagement(onChange)

  const {html, loadPreview, previewStale} = usePreview(value, onRenderPreview)

  useEffect(() => {
    // we have to be careful here - loading preview sets state which causes a render which can cause an infinite loop,
    // however that should be prevented by previewStale.current being set immediately in loadPreview
    if (viewMode === 'preview' && previewStale.current) void loadPreview()
  })

  // Track textarea height while in edit mode so the preview pane can match it.
  // `inputHeight` is only consumed by the preview pane (see `minHeight` below), so we keep the
  // live measurement in a ref and only commit it to state when switching to preview. Writing to
  // state on every ResizeObserver callback would re-render the whole editor each time the textarea
  // auto-grows while typing — wasted work in edit mode that adds INP cost on large pages such as
  // the PR diff view. See INP pattern R3 (no state updates in a ResizeObserver).
  const [inputHeight, setInputHeight] = useState(0)
  const inputHeightRef = useRef(0)
  useLayoutEffect(() => {
    const input = inputRef.current
    if (!input || viewMode !== 'edit') return

    inputHeightRef.current = input.offsetHeight

    const observer = new ResizeObserver(([entry]) => {
      // Read the border-box height to match the `offsetHeight` seed above (both include padding + border), so the
      // committed height is consistent whether or not a resize has fired. Fall back to `contentRect` (content-box)
      // only where `borderBoxSize` isn't available.
      if (entry) inputHeightRef.current = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height
    })
    observer.observe(input)
    return () => observer.disconnect()
  }, [inputRef, viewMode])

  // Commit the most recent edit-mode height when entering preview so the preview pane can reserve
  // at least that much space (avoids a layout jump on toggle).
  useLayoutEffect(() => {
    if (viewMode === 'preview') setInputHeight(inputHeightRef.current)
  }, [viewMode])

  useImperativeHandle(
    ref,
    () => ({
      focus: opts => inputRef.current?.focus(opts),
      moveCursorToEnd: () => {
        if (inputRef.current) {
          const length = inputRef.current.value.length
          inputRef.current.setSelectionRange(length, length)
        }
      },
      scrollIntoView: opts => containerRef.current?.scrollIntoView(opts),
      reset: () => reset(),
      inputRef,
      // satisfy the type monster
      [handleBrand]: undefined,
    }),
    [containerRef, inputRef, reset],
  )

  const handleFileRejected = useCallback(
    (rejectedFiles: File[]) => {
      const extensions = []
      // Send separate event for each rejected file
      for (const file of rejectedFiles) {
        const fileExtension = file.name.includes('.') ? `.${file.name.split('.').pop()}` : 'no-extension'
        sendEvent('markdown_editor.file_upload.rejected_file_type', {
          // eslint-disable-next-line camelcase
          rejected_file_type: fileExtension,
          // eslint-disable-next-line camelcase
          mime_type: file.type || 'unknown',
        })
        extensions.push(fileExtension)
      }
      onFilesRejected?.(rejectedFiles, extensions)
    },
    [onFilesRejected],
  )

  const fileHandler = useFileHandling({
    emitChange,
    value,
    inputRef,
    disabled,
    onUploadFile,
    acceptedFileTypes,
    onFilesRejected: handleFileRejected,
  })

  const isUploadingFiles = fileHandler?.uploadProgress !== undefined
  const listEditor = useListEditing({emitChange})
  const indenter = useIndenting({emitChange})
  const preserveSpacingIfWithinSuggestedChange = usePreserveSpacingIfWithinSuggestedChange({emitChange})

  const savedRepliesRef = useRef<SavedRepliesHandle>(null)

  const savedRepliesContext = useMemo(
    () =>
      savedReplies
        ? {
            savedReplies,
            onSelect: (reply: SavedReply) => {
              // need to wait a tick to run after the selectmenu finishes closing
              requestAnimationFrame(() => emitChange(reply.content))
            },
            onOpen: onSavedRepliesOpen,
            ref: savedRepliesRef,
          }
        : null,
    [emitChange, onSavedRepliesOpen, savedReplies],
  )

  const inputCompositionProps = useIgnoreKeyboardActionsWhileComposing(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      const format = formattingToolsRef.current
      if (disabled) return

      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (e.ctrlKey && e.key === '.') {
        // saved replies are always Control, even on Mac
        savedRepliesRef.current?.openMenu()
        e.preventDefault()
        e.stopPropagation()
      } else if (isModifierKey(e)) {
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        if (e.key === 'Enter' && !isUploadingFiles) onPrimaryAction?.()
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        else if (e.key === 'b') format?.bold()
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        else if (e.key === 'i') format?.italic()
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        else if (e.shiftKey && e.key === '.') format?.quote()
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        else if (e.key === 'e') format?.code()
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        else if (e.key === 'k') format?.link()
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        else if (e.key === '8') format?.unorderedList()
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        else if (e.shiftKey && e.key === '7') format?.orderedList()
        // on some layouts, such as AZERTY, the key is registered as uppercase when shift is pressed
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        else if (e.shiftKey && ['l', 'L'].includes(e.key)) format?.taskList()
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        else if (e.shiftKey && ['p', 'P'].includes(e.key)) onChangeViewMode?.('preview')
        else return

        e.preventDefault()
        e.stopPropagation()
      } else {
        listEditor.onKeyDown(e)
        indenter.onKeyDown(e)
        preserveSpacingIfWithinSuggestedChange.onKeyDown(e)
      }
    },
  )

  const onChangeViewModeRef = useRef(onChangeViewMode)
  useEffect(() => {
    onChangeViewModeRef.current = onChangeViewMode
  })

  useEffect(() => {
    if (viewMode === 'preview') {
      editorsInPreviewMode.push(id)

      const handler = (e: KeyboardEvent) => {
        if (
          !e.defaultPrevented &&
          editorsInPreviewMode.at(-1) === id &&
          isModifierKey(e) &&
          e.shiftKey &&
          // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
          e.key === 'p'
        ) {
          onChangeViewModeRef.current?.('edit')
          // eslint-disable-next-line @eslint-react/web-api-no-leaked-timeout
          setTimeout(() => inputRef.current?.focus())
          e.preventDefault()
        }
      }
      document.addEventListener('keydown', handler)

      return () => {
        document.removeEventListener('keydown', handler)
        // Performing the filtering in the cleanup callback allows it to happen also when
        // the user clicks the toggle button, not just on keyboard shortcut
        editorsInPreviewMode = editorsInPreviewMode.filter(id_ => id_ !== id)
      }
    }
  }, [viewMode, id, inputRef])

  const formattingToolsRef = useRef<FormattingTools>(null)

  // If we don't memoize the context object, every child will rerender on every render even if memoized.
  const context = useMemo(
    () => ({
      disabled,
      required,
      fileDraggedOver: fileHandler?.isDraggedOver ?? false,
      fileUploadProgress: fileHandler?.uploadProgress,
      uploadButtonProps: fileHandler?.clickTargetProps ?? null,
      errorMessage: fileHandler?.errorMessage,
      previewMode: viewMode === 'preview',
    }),
    [
      disabled,
      required,
      fileHandler?.isDraggedOver,
      fileHandler?.uploadProgress,
      fileHandler?.clickTargetProps,
      fileHandler?.errorMessage,
      viewMode,
    ],
  )

  const onViewSelect = (mode: MarkdownViewMode) => {
    sendEvent(`markdown_editor.select_view`, {
      mode,
      ['from_mode']: viewMode,
      ['trigger']: 'view_switcher',
      ['did_change']: viewMode !== mode,
      ['feature_flag_markdown_experience_hybrid_editor']: hybridEditorEnabled,
    })
    onChangeViewMode?.(mode)
  }

  const viewSwitch = (
    <ViewSwitch
      selectedView={viewMode}
      onViewSelect={onViewSelect}
      disabled={fileHandler?.uploadProgress !== undefined}
      onLoadPreview={loadPreview}
    />
  )

  return (
    <FormattingToolsContext value={formattingToolsRef}>
      <MarkdownEditorContext value={context}>
        <FormattingTools ref={formattingToolsRef} forInputId={id} />
        <div
          className={clsx(
            className,
            viewMode === 'edit' ? styles.inputWrapper : styles.previewWrapper,
            disabled && styles.disabled,
            fullHeight && styles.fullHeight,
          )}
        >
          <div className={styles.header}>
            {!toolbarRedesignEnabled && <div className={styles.viewSwitchWrapper}>{viewSwitch}</div>}

            <SavedRepliesContext value={savedRepliesContext}>
              {viewMode === 'edit' ? (
                (slots.toolbar ?? (
                  <CoreToolbar>
                    <DefaultToolbarButtons />
                  </CoreToolbar>
                ))
              ) : (
                <div style={{flex: 1}} />
              )}
            </SavedRepliesContext>

            {toolbarRedesignEnabled && <div className={styles.redesignedViewSwitchWrapper}>{viewSwitch}</div>}
          </div>
          <MarkdownInput
            value={value}
            onChange={!useOnInput ? onInputChange : undefined}
            onInput={useOnInput ? onInputChange : undefined}
            onFocus={onInputFocus}
            emojiSuggestions={emojiSuggestions}
            emojiTone={emojiTone}
            mentionSuggestions={mentionSuggestions}
            referenceSuggestions={referenceSuggestions}
            disabled={disabled}
            placeholder={placeholder}
            labelledBy={labelledBy}
            aria-describedby={describedBy}
            id={id}
            maxLength={maxLength}
            ref={inputRef}
            fullHeight={fullHeight}
            isDraggedOver={fileHandler?.isDraggedOver ?? false}
            minHeightLines={minHeightLines}
            maxHeightLines={maxHeightLines}
            visible={viewMode === 'edit'}
            monospace={monospace}
            required={required}
            name={name}
            pasteUrlsAsPlainText={pasteUrlsAsPlainText}
            generatingText={generatingText}
            key={inputKey}
            {...inputCompositionProps}
            {...fileHandler?.pasteTargetProps}
            {...fileHandler?.dropTargetProps}
          />
          {viewMode === 'edit' && fileHandler?.errorMessage ? (
            <ErrorBanner>{fileHandler.errorMessage}</ErrorBanner>
          ) : (
            banner
          )}
          {viewMode === 'preview' && (
            <div
              aria-live="polite"
              tabIndex={-1}
              className={clsx(styles.previewViewerWrapper, fullHeight && styles.fullHeight)}
              style={{minHeight: inputHeight}}
            >
              {/* eslint-disable-next-line @github-ui/dotcom-primer/no-title-case -- baselined during linter rollout */}
              <h2 className={styles.previewHeader}>Rendered Markdown Preview</h2>
              <MarkdownViewer
                /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- Static string literal is safe */
                verifiedHTML={html || ('Nothing to preview' as SafeHTMLString)}
                loading={html === null}
                teamHovercardsEnabled={teamHovercardsEnabled}
                openLinksInNewTab
              />
            </div>
          )}
        </div>
        {footer}
      </MarkdownEditorContext>
    </FormattingToolsContext>
  )
}
ClassicMarkdownEditor.displayName = 'ClassicMarkdownEditor'

type HybridMarkdownEditorWithFooterProps = {
  footer: React.ReactNode
}

const HybridMarkdownEditorWithFooter = ({
  ref,
  hybridEditorProps,
  ...props
}: MarkdownEditorProps & HybridMarkdownEditorWithFooterProps) => {
  const {footer, banner, generatingText, ...markdownEditorProps} = props

  const context = useMemo(
    () => ({
      disabled: props.disabled ?? false,
      required: props.required ?? false,
      uploadButtonProps: null,
      fileDraggedOver: false,
      previewMode: false,
    }),
    [props.disabled, props.required],
  )

  const hybridEditorRef = useRef<HybridMarkdownEditorRef>(null)

  useImperativeHandle(ref, () => ({
    focus: () => hybridEditorRef.current?.focus(),
    moveCursorToEnd: () => hybridEditorRef.current?.moveCursorToEnd(),
    reset: () => hybridEditorRef.current?.reset(),
    scrollIntoView: opts => hybridEditorRef.current?.scrollIntoView(opts),
    // Use the hybrid editor's container element for positioning floating panels
    get inputRef() {
      return hybridEditorRef.current?.containerRef ?? {current: null}
    },
    [handleBrand]: undefined,
  }))

  return (
    <MarkdownEditorContext value={context}>
      <HybridMarkdownEditor
        ref={hybridEditorRef}
        {...markdownEditorProps}
        {...hybridEditorProps}
        isGenerating={generatingText}
        banner={banner}
      />
      {footer}
    </MarkdownEditorContext>
  )
}
HybridMarkdownEditorWithFooter.displayName = 'HybridMarkdownEditorWithFooter'

const MarkdownEditor = ({ref, ...props}: MarkdownEditorProps) => {
  const {
    disabled = false,
    'aria-describedby': describedBy,
    fullHeight,
    viewMode: controlledViewMode,
    onChangeViewMode: controlledSetViewMode,
    children,
  } = props

  const [slots, childrenWithoutSlots] = useSlots(children, {
    toolbar: Toolbar,
    actions: Actions,
    label: Label,
    footer: Footer,
  })

  const [uncontrolledViewMode, setUncontrolledViewMode] = useState<MarkdownViewMode>('edit')
  const [viewMode, onChangeViewMode] =
    controlledViewMode === undefined
      ? [uncontrolledViewMode, setUncontrolledViewMode]
      : [controlledViewMode, controlledSetViewMode]

  const editorType = getEditorType(viewMode)

  const descriptionId = useId()

  const footer = slots.footer ?? (
    <CoreFooter>{isValidElement(slots.actions) && slots.actions.props.children}</CoreFooter>
  )

  const commonProps = {
    footer,
    // TODO: Add more common props here if needed in the future
    'aria-describedby': describedBy ? `${descriptionId} ${describedBy}` : descriptionId,
  }

  useEffect(
    () =>
      sendEvent('markdown_editor.mount', {
        editorType,
        ['feature_flag_markdown_experience_hybrid_editor']: isFeatureEnabled('markdown_experience_hybrid_editor'),
      }),
    [editorType],
  )

  const containerRef = useRef<HTMLDivElement>(null)

  const editor =
    editorType === 'hybrid' ? (
      <HybridMarkdownEditorWithFooter {...props} ref={ref} onChangeViewMode={onChangeViewMode} {...commonProps} />
    ) : (
      <ClassicMarkdownEditor
        {...props}
        viewMode={viewMode === 'hybrid' ? 'edit' : viewMode}
        onChangeViewMode={onChangeViewMode}
        ref={ref}
        containerRef={containerRef}
        {...commonProps}
      >
        {slots.toolbar}
      </ClassicMarkdownEditor>
    )

  return (
    <fieldset
      aria-disabled={disabled /* if we set disabled={true}, we can't enable the buttons that should be enabled */}
      className={clsx(styles.fieldSet, fullHeight && styles.fullHeight)}
    >
      <div className={styles.hidden}>{childrenWithoutSlots}</div>

      {slots.label}

      <div ref={containerRef} className={styles.container}>
        <span className="sr-only" id={descriptionId} aria-live="polite">
          {`Markdown input: ${
            viewMode === 'hybrid'
              ? 'hybrid mode selected.'
              : viewMode === 'preview'
                ? ' preview mode selected.'
                : ' edit mode selected.'
          }`}
        </span>
        {editor}
      </div>
    </fieldset>
  )
}
MarkdownEditor.displayName = 'MarkdownEditor'

export default MarkdownEditor
